<template>
  <div class="register-container">
    <div class="chat-container">
      <!-- En-tête avec logo et titre -->
      <div class="chat-header">
        <div class="bot-avatar">
          <img src="../assets/buddi-avatar.png" alt="BuddiBot">
        </div>
        <h1>Création de compte BuddiBot</h1>
      </div>

      <!-- Zone de chat -->
      <div class="chat-messages" ref="chatMessages">
        <transition-group name="message-fade">
          <div v-for="(message, index) in messages" :key="index"
               :class="['message', message.sender === 'bot' ? 'bot-message' : 'user-message']">
            <div v-if="message.sender === 'bot'" class="bot-avatar-small">
              <img src="../assets/buddi-avatar.png" alt="BuddiBot">
            </div>
            <div class="message-content" v-html="message.displayContent || message.content"></div>
          </div>
        </transition-group>

        <!-- Indicateur de saisie -->
        <div v-if="isTyping" class="message bot-message typing-indicator">
          <div class="bot-avatar-small">
            <img src="../assets/buddi-avatar.png" alt="BuddiBot">
          </div>
          <div class="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      <!-- Zone d'interaction -->
      <div class="chat-interaction">
        <div v-if="currentOptions.length > 0" class="options-container">
          <button
            v-for="(option, index) in currentOptions"
            :key="index"
            @click="handleOptionClick(option)"
            class="option-button"
            :class="{ 'primary': option.primary }"
          >
            {{ option.text }}
          </button>
        </div>

        <!-- Connexion Discord -->
        <div v-if="currentStep === 'discord'" class="auth-container">
          <button @click="connectDiscord" class="discord-button">
            <img src="../assets/discord-mark-white.svg" alt="Discord logo" class="discord-logo">
            Se connecter avec Discord
          </button>
        </div>

        <!-- Connexion Twitch -->
        <div v-if="currentStep === 'twitch'" class="auth-container">
          <button @click="connectTwitch" class="twitch-button">
            <img src="../assets/twitch-logo.svg" alt="Twitch logo" class="twitch-logo">
            Se connecter avec Twitch
          </button>
        </div>
      </div>

      <!-- Barre de progression -->
      <div class="progress-bar">
        <div class="progress-step" :class="{ 'active': stepIndex >= 0, 'completed': stepIndex > 0 }">CGU</div>
        <div class="progress-step" :class="{ 'active': stepIndex >= 1, 'completed': stepIndex > 1 }">Discord</div>
        <div class="progress-step" :class="{ 'active': stepIndex >= 2, 'completed': stepIndex > 2 }">Twitch</div>
        <div class="progress-step" :class="{ 'active': stepIndex >= 3, 'completed': stepIndex > 3 }">Fonctionnalités</div>
        <div class="progress-step" :class="{ 'active': stepIndex >= 4 }">Terminé</div>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '../services/api'

export default {
  name: 'Register',
  data() {
    return {
      messages: [],
      messageQueue: [],
      isProcessingQueue: false,
      isTyping: false,
      currentStep: 'welcome',
      currentOptions: [],
      stepIndex: 0,
      discordData: null,
      twitchData: null,
      cguAccepted: false
    }
  },
  created() {
    // Vérifier si l'utilisateur est déjà connecté ET n'est pas en cours d'inscription
    const token = localStorage.getItem('discord_token');
    const isRegistering = localStorage.getItem('is_registering');

    // Si l'utilisateur est connecté mais n'est pas en cours d'inscription, le rediriger vers le dashboard
    if (token && !isRegistering) {
      this.$router.push('/dashboard');
      return;
    }

    // Démarrer la conversation
    this.startConversation();
  },
  mounted() {
    // Ajouter un écouteur d'événements pour les messages de la fenêtre popup Twitch
    window.addEventListener('message', this.handlePopupMessage);

    // Vérifier si on revient d'une authentification Discord (token dans le fragment) ou Twitch (code dans les paramètres)

    // Vérifier d'abord le fragment d'URL pour l'authentification Discord
    if (window.location.hash.includes('access_token')) {
      const fragment = new URLSearchParams(window.location.hash.substring(1));
      const token = fragment.get('access_token');

      if (token) {
        this.handleDiscordTokenCallback(token);
        // Nettoyer l'URL
        window.history.replaceState({}, document.title, '/register');
        return;
      }
    }

    // Vérifier ensuite les paramètres d'URL pour l'authentification Twitch
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state === 'twitch_auth') {
      this.handleTwitchCallback(code);
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, '/register');
    }
  },
  beforeUnmount() {
    // Nettoyer l'indicateur d'inscription en cours si l'utilisateur quitte la page
    localStorage.removeItem('is_registering');
    // Supprimer l'écouteur d'événements pour éviter les fuites de mémoire
    window.removeEventListener('message', this.handlePopupMessage);
  },
  methods: {
    // Traiter les messages reçus de la fenêtre popup Twitch
    handlePopupMessage(event) {
      
      // Vérifier si le message vient de la fenêtre popup Twitch
      if (event.data && event.data.type) {
        if (event.data.type === 'twitch_auth_success') {
          
          // Marquer que nous avons reçu un message
          localStorage.setItem('twitch_message_received', 'true');
          
          // Vérifier si l'état correspond à celui généré
          const savedState = localStorage.getItem('twitch_auth_state');
          
          if (event.data.state && (event.data.state === savedState || !savedState)) {
            // Traiter le code immédiatement
            this.handleTwitchCallback(event.data.code);
            
            // Stocker le code pour être sûr qu'il est disponible
            localStorage.setItem('twitch_auth_code', event.data.code);
            
            // Nous n'avons plus les données utilisateur Twitch à ce stade
            // Le backend se chargera de récupérer ces informations
            this.addBotMessage("Connexion Twitch réussie ! Finalisation de votre inscription...");
          } else {
            console.warn('Les états ne correspondent pas, possible tentative CSRF');
            this.addBotMessage("Une erreur de sécurité s'est produite lors de la connexion avec Twitch. Veuillez réessayer.");
          }
        } else if (event.data.type === 'twitch_auth_error') {
          // Afficher l'erreur
          console.error('Erreur Twitch:', event.data.error, event.data.errorDescription);
          this.addBotMessage(`Une erreur s'est produite lors de la connexion avec Twitch: ${event.data.errorDescription || event.data.error}. Veuillez réessayer.`);
        }
      }
    },
    startConversation() {
      // Définir le flag indiquant que l'utilisateur est en cours d'inscription
      localStorage.setItem('is_registering', 'true');
      
      this.currentStep = 'welcome';
      this.addBotMessage("Bonjour et bienvenue dans le processus d'inscription à BuddiBot !");
      this.addBotMessage("Je suis votre assistant virtuel et je vais vous guider tout au long de ce processus.");
      this.addBotMessage("showCGU", true); // Utiliser la commande 'showCGU' pour déclencher l'affichage des CGU
    },
    
    showCGU() {
      this.currentStep = 'cgu';
      this.stepIndex = 0;
      this.currentOptions = []; // Réinitialiser les options pour s'assurer qu'aucun bouton n'est affiché
      this.addBotMessage("Avant de commencer, je dois vous présenter nos Conditions Générales d'Utilisation.");
      
      // Premier délai pour afficher le résumé des CGU
      setTimeout(() => {
        this.addBotMessage(`
          <div class="cgu-container">
            <h3>Résumé des CGU</h3>
            <p>En utilisant BuddiBot, vous acceptez :</p>
            <ul>
              <li>De respecter les conditions d'utilisation de Twitch et Discord</li>
              <li>Que nous collections certaines données pour le fonctionnement du service</li>
              <li>De ne pas utiliser le bot pour du contenu illégal ou nuisible</li>
            </ul>
            <p>Pour consulter l'intégralité des CGU, <a href="/cgu" target="_blank">cliquez ici</a>.</p>
          </div>
        `);
        
        // Deuxième délai pour afficher les boutons d'acceptation après la présentation du résumé
        setTimeout(() => {
          this.currentOptions = [
            { text: "J'accepte les CGU", value: 'accept', primary: true },
            { text: "Je refuse", value: 'decline' }
          ];
        }, 5000); // Délai supplémentaire après l'affichage du résumé
      }, 1000);
    },
    
    // Méthode pour démarrer l'authentification Discord
    startDiscordAuth() {
      this.stepIndex = 1;
      this.addBotMessage("Pour commencer, connectez-vous avec votre compte Discord.");
      this.addBotMessage("Cliquez sur le bouton ci-dessous pour vous authentifier.");
      
      setTimeout(() => {
            this.currentStep = 'discord';
        }, 5000);
    },

    startTwitchAuth() {
      setTimeout(() => {
        this.currentStep = 'twitch';
        this.stepIndex = 2;
      }, 5000);

      // Récupérer les données Discord de l'utilisateur
      const userData = JSON.parse(localStorage.getItem('discord_user'));
      const globalName = userData?.global_name || userData?.username || '';

      // Message personnalisé avec le nom d'utilisateur en couleur secondaire
      if (globalName) {
        this.addBotMessage(`Super <span style="color: #eda526; font-weight: bold;">${globalName}</span> ! 🎉 Je suis enchanté de faire votre connaissance. Votre compte Discord est maintenant lié.`);
      } else {
        this.addBotMessage("Super ! 🎉 Votre compte Discord est maintenant lié.");
      }

      setTimeout(() => {
        this.addBotMessage("Maintenant, connectons votre compte Twitch pour activer toutes les fonctionnalités du bot.");
      }, 1000);
    },

    showFeatures() {
      this.currentStep = 'features';
      this.stepIndex = 3;

      // Démarrer la conversation sur les étapes suivantes
      setTimeout(() => {
        this.addBotMessage(`Voici les étapes importantes à suivre pour profiter pleinement de BuddiBot :`);
        
        // Étape 1 - Activation des modules (en un seul message avec HTML)
        setTimeout(() => {
          this.addBotMessage(`
            <div class="steps-container">
              <h2>1. Activez les modules qui vous intéressent dans le tableau de bord :</h2>
            </div>
          `);
          
          // Étape 2 - Configuration des logs (en un seul message avec HTML)
          setTimeout(() => {
            this.addBotMessage(`
              <div class="steps-container">
                <h2>2. Créez un salon privé sur votre serveur Discord pour recevoir mes logs :</h2>
                <ul>
                  <li>Donnez moi les permissions d'écrire dans ce salon</li>
                  <li>Les logs vous aideront à suivre les actions de BuddiBot</li>
                </ul>
              </div>
            `);
            
            // Proposer d'accéder au dashboard
            setTimeout(() => {
              this.currentOptions = [
                { text: "Accéder au Dashboard", value: 'dashboard', primary: true }
              ];
            }, 5000);
          }, 1500);
        }, 1000);
      }, 1000);
    },

    // Méthode pour terminer la présentation des modules et proposer d'accéder au dashboard
    finishModulePresentation() {
      this.currentStep = 'features';
      this.addBotMessage("Parfait ! Votre compte est maintenant configuré et vous pouvez accéder à votre tableau de bord pour commencer à utiliser BuddiBot.");
      this.currentOptions = [
        { text: "Accéder au Dashboard", value: 'dashboard', primary: true }
      ];
    },


  
  // Méthode pour envoyer la requête de création de compte au backend
  async createSubscription() {
    try {
      // Récupérer les données Discord et le code Twitch stockés pendant le processus d'inscription
      const discordUser = JSON.parse(localStorage.getItem('discord_user'));
      const discordToken = localStorage.getItem('discord_token');
      const twitchCode = localStorage.getItem('twitch_auth_code');

      if (!discordUser) {
        console.error('Informations Discord incomplètes pour la création de compte');
        this.handleSubscriptionError('Informations Discord incomplètes');
        return;
      }

      if (!twitchCode) {
        console.error('Code Twitch manquant pour la création de compte');
        this.handleSubscriptionError('Code Twitch manquant');
        return;
      }

      // Construire l'objet de requête pour l'API avec les informations essentielles
      // Le backend se chargera de récupérer les informations complètes de l'utilisateur Twitch
      const subscriptionData = {
        discordUserId: discordUser.id,
        discordUsername: discordUser.username || discordUser.global_name,
        accessToken: discordToken,
        twitchAppCode: twitchCode,
        // Inclure l'URL de redirection pour que le backend puisse échanger le code contre un token
        redirectUri: process.env.VUE_APP_CALLBACK_URL?.replace('callback', 'twitch-callback') || ''
      };

      // Envoyer les données à l'API en arrière-plan
      const response = await api.post('/Subscriptions/create', subscriptionData);

      if (response.data && !response.data.error) {
        //console.log('Compte créé avec succès:', response.data);
        // Stocker l'information que le compte a été créé avec succès
        localStorage.setItem('account_created', 'true');
        
        // Mettre à jour l'interface utilisateur si la présentation des modules est terminée
        if (this.currentStep === 'features') {
          this.addBotMessage("🎉 Félicitations ! Votre compte a été créé avec succès.");
          this.currentOptions = [
            { text: "Accéder au Dashboard", value: 'dashboard', primary: true }
          ];
        }
      } else {
        // Extraire le message d'erreur de la réponse
        const errorMessage = response.data?.error?.message || 
                            response.data?.message || 
                            'Erreur inconnue lors de la création du compte';
        this.handleSubscriptionError(errorMessage);
      }
    } catch (error) {
      // Gérer les erreurs de requête
      let errorMessage = 'Erreur inconnue lors de la création du compte';
      
      // Vérifier si l'erreur a une réponse avec des données
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error?.message || 
                       error.response.data.message || 
                       error.message || 
                       errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.handleSubscriptionError(errorMessage);
    }
  },
  
  // Méthode pour gérer les erreurs de création de compte
  handleSubscriptionError(errorMessage) {
    console.error('Erreur lors de la création du compte:', errorMessage);
    
    // Stocker l'information qu'il y a eu une erreur lors de la création du compte
    localStorage.setItem('account_creation_error', errorMessage);
    
    // Afficher un message d'erreur à l'utilisateur
    this.addBotMessage(`<span style="color: #ff5252;">❌ Une erreur est survenue lors de la création de votre compte: ${errorMessage}</span>`);
    
    // Proposer à l'utilisateur de se connecter au dashboard (il pourra réessayer plus tard)
    this.addBotMessage("Vous pouvez essayer de vous connecter au dashboard pour réessayer.");
    
    // Mettre à jour les options disponibles
    this.currentOptions = [
      { text: "Accéder au Dashboard", value: 'dashboard', primary: true }
    ];
  },

  // Méthode pour finaliser l'inscription et rediriger vers le dashboard
  async finishRegistration() {
    this.currentStep = 'finished';
    this.stepIndex = 4;

    // Vérifier si le compte a déjà été créé avec succès
    const accountCreated = localStorage.getItem('account_created') === 'true';
    const accountCreationError = localStorage.getItem('account_creation_error');

    if (accountCreated) {
      // Supprimer le flag d'inscription puisque le processus est terminé
      localStorage.removeItem('is_registering');
      localStorage.removeItem('account_created');
      
      this.addBotMessage("🎉 Félicitations ! Votre compte a été créé avec succès.");
      this.addBotMessage("Vous allez être redirigé vers votre tableau de bord dans quelques instants...");

      // Redirection vers le dashboard après un court délai
      setTimeout(() => {
        this.$router.push('/dashboard');
      }, 3000);
    } else if (accountCreationError) {
      // Afficher un message d'erreur si la création du compte a échoué
      localStorage.removeItem('account_creation_error');
      
      this.addBotMessage(`⚠️ Une erreur est survenue lors de la création de votre compte: ${accountCreationError}`);
      console.error('Erreur de création de compte:', accountCreationError);
      
      this.addBotMessage("Vous pouvez essayer de vous connecter directement ou recommencer le processus d'inscription.");
      
      // Proposer des options à l'utilisateur
      this.currentOptions = [
        { text: "Se connecter", value: 'login', primary: true },
        { text: "Recommencer", value: 'restart' }
      ];
    } else {
      // Si la création du compte est toujours en cours, essayer de la finaliser
      this.addBotMessage("Finalisation de votre inscription...");
      
      try {
        await this.createSubscription();
        
        // Vérifier à nouveau si le compte a été créé avec succès
        if (localStorage.getItem('account_created') === 'true') {
          localStorage.removeItem('is_registering');
          localStorage.removeItem('account_created');
          
          this.addBotMessage("🎉 Félicitations ! Votre compte a été créé avec succès.");
          this.addBotMessage("Vous allez être redirigé vers votre tableau de bord dans quelques instants...");

          // Redirection vers le dashboard après un court délai
          setTimeout(() => {
            this.$router.push('/dashboard');
          }, 3000);
        } else {
          // Si la création a échoué, afficher un message d'erreur
          this.addBotMessage("⚠️ Une erreur est survenue lors de la création de votre compte. Veuillez réessayer.");
          
          // Proposer des options à l'utilisateur
          this.currentOptions = [
            { text: "Se connecter", value: 'login', primary: true },
            { text: "Recommencer", value: 'restart' }
          ];
        }
      } catch (error) {
        console.error('Erreur lors de la finalisation de l\'inscription:', error);
        this.addBotMessage(`⚠️ Une erreur est survenue lors de la création de votre compte: ${error.message || 'Erreur inconnue'}`);
        this.addBotMessage("Vous pouvez essayer de vous connecter directement ou recommencer le processus d'inscription.");
        
        // Proposer des options à l'utilisateur
        this.currentOptions = [
          { text: "Se connecter", value: 'login', primary: true },
          { text: "Recommencer", value: 'restart' }
        ];
      }
    }
  },
    // Ajoute un message à la file d'attente
    addBotMessage(content, isCommand = false) {
      // Ajouter le message à la file d'attente
      this.messageQueue.push({ content, isCommand });

      // Si la file n'est pas en cours de traitement, démarrer le traitement
      if (!this.isProcessingQueue) {
        this.processNextInQueue();
      }
    },

    // Traite le prochain message dans la file d'attente
    processNextInQueue() {
      if (this.messageQueue.length === 0) {
        this.isProcessingQueue = false;
        return;
      }

      this.isProcessingQueue = true;
      const nextItem = this.messageQueue.shift();

      // Si c'est une commande spéciale, l'exécuter
      if (nextItem.isCommand) {
        if (nextItem.content === 'showCGU') {
          this.showCGU();
          this.processNextInQueue();
          return;
        }
      }

      // Sinon, afficher le message
      this.displayBotMessage(nextItem.content);
    },

    // Affiche un message avec animation
    displayBotMessage(content) {
      this.isTyping = true;

      setTimeout(() => {
        this.isTyping = false;

        // Si le contenu contient du HTML, on le traite différemment
        const isHTML = content.includes('<') && content.includes('>');

        const onMessageComplete = () => {
          // Passer au message suivant dans la file d'attente
          this.processNextInQueue();
        };

        if (isHTML) {
          // Pour le contenu HTML, on utilise une approche simplifiée
          const newMessage = {
            content,
            displayContent: '',
            sender: 'bot',
            isHTML: true
          };

          this.messages.push(newMessage);

          // Faire défiler vers le bas
          this.$nextTick(() => {
            if (this.$refs.chatMessages) {
              this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
            }
          });

          // On divise le contenu HTML en segments visibles
          const parser = new DOMParser();
          const doc = parser.parseFromString(`<div>${content}</div>`, 'text/html');
          const container = doc.querySelector('div');
          const elements = Array.from(container.children);

          // Fonction pour afficher progressivement les éléments HTML
          let elementIndex = 0;
          const showNextElement = () => {
            if (elementIndex < elements.length) {
              // Construire le HTML progressivement
              const visibleElements = elements.slice(0, elementIndex + 1);
              const tempContainer = document.createElement('div');
              visibleElements.forEach(el => tempContainer.appendChild(el.cloneNode(true)));
              newMessage.displayContent = tempContainer.innerHTML;

              // Faire défiler vers le bas
              this.$nextTick(() => {
                if (this.$refs.chatMessages) {
                  this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
                }
              });

              elementIndex++;
              setTimeout(showNextElement, 300); // Délai entre chaque élément
            } else {
              // Assurer que le contenu final est exactement le contenu original
              newMessage.displayContent = content;
              onMessageComplete();
            }
          };

          // Si le contenu HTML est simple ou n'a pas d'éléments enfants, on l'affiche directement
          if (elements.length <= 1) {
            setTimeout(() => {
              newMessage.displayContent = content;
              onMessageComplete();
            }, 300);
          } else {
            showNextElement();
          }
        } else {
          // Pour le texte simple, on peut faire un vrai effet de typing caractère par caractère
          const newMessage = {
            content,
            displayContent: '',
            sender: 'bot',
            isHTML: false
          };

          this.messages.push(newMessage);

          // Faire défiler vers le bas
          this.$nextTick(() => {
            if (this.$refs.chatMessages) {
              this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
            }
          });

          // Utiliser un tableau de caractères pour l'effet de typing
          const chars = content.split('');
          // Vitesse de base adaptée: plus rapide pour les longs textes, mais pas trop
          const baseSpeed = Math.max(30, Math.min(70, 1500 / chars.length));

          // Fonction récursive pour ajouter un caractère à la fois
          const typeNextChar = (index = 0) => {
            // S'assurer que le message est toujours dans le DOM
            const messageIndex = this.messages.indexOf(newMessage);
            if (messageIndex === -1) {
              onMessageComplete();
              return;
            }

            if (index < chars.length) {
              // Ajouter le prochain caractère - méthode compatible avec Vue 3
              newMessage.displayContent = content.substring(0, index + 1);

              // Faire défiler vers le bas pendant le typing
              this.$nextTick(() => {
                if (this.$refs.chatMessages) {
                  this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
                }
              });

              // Calculer le délai pour le prochain caractère
              const currentChar = chars[index];
              const isPunctuation = ['.', ',', '!', '?', ':', ';'].includes(currentChar);
              const randomFactor = 0.7 + Math.random() * 0.6; // Variation entre 70% et 130%
              let delay = Math.round(baseSpeed * randomFactor);

              // Ajouter un délai supplémentaire pour les ponctuations
              if (isPunctuation) {
                delay += 150;
              }

              // Programmer l'ajout du prochain caractère
              setTimeout(() => typeNextChar(index + 1), delay);
            } else {
              // Tous les caractères ont été affichés
              onMessageComplete();
            }
          };

          // Démarrer l'effet de typing avec un court délai initial
          setTimeout(() => typeNextChar(), 100);
        }
      }, 1000);
    },

    addUserMessage(content) {
      this.messages.push({
        content,
        sender: 'user'
      });

      // Faire défiler vers le bas
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
        }
      });
    },
    
    handleOptionClick(option) {
      this.addUserMessage(option.text);
      this.currentOptions = [];

      if (this.currentStep === 'cgu') {
        if (option.value === 'accept') {
          this.cguAccepted = true;
          this.startDiscordAuth();
        } else {
          this.addBotMessage("Je comprends. Vous pouvez revenir quand vous serez prêt à accepter les CGU pour utiliser BuddiBot.");
          setTimeout(() => {
            this.$router.push('/');
          }, 3000);
        }
      } else if (this.currentStep === 'features') {
        if (option.value === 'dashboard') {
          this.finishRegistration();
        }
      } else if (this.currentStep === 'module_gta_rp') {
        // Gestion des réponses pour le module GTA RP
        if (option.value === 'gta_rp_yes') {
          this.addBotMessage(`
            <div class="feature-card">
              <div class="feature-icon">🎭</div>
              <div class="feature-text">
                <h4>Module Roleplay</h4>
                <p>BuddiBot peut surveiller les streams GTA RP et détecter automatiquement les scènes importantes, les rencontres entre joueurs et les moments forts pour faciliter la création de highlights.</p>
              </div>
            </div>
          `);
          
          // Passer au module suivant - Modération
          setTimeout(() => {
            this.showModerationModule();
          }, 2000);
        } else {
          // L'utilisateur n'est pas intéressé par le module GTA RP, passer au module suivant
          this.showModerationModule();
        }
      } else if (this.currentStep === 'module_moderation') {
        // Gestion des réponses pour le module Modération
        if (option.value === 'moderation_yes') {
          this.addBotMessage(`
            <div class="feature-card">
              <div class="feature-icon">🛡️</div>
              <div class="feature-text">
                <h4>Module Modération</h4>
                <p>BuddiBot peut modérer automatiquement votre chat en filtrant le spam, les liens malveillants et les comportements toxiques. Vous pouvez personnaliser les règles de modération selon vos besoins.</p>
              </div>
            </div>
          `);
          
          // Passer au module suivant - Jeux et Interactions
          setTimeout(() => {
            this.showGamesModule();
          }, 2000);
        } else {
          // L'utilisateur n'est pas intéressé par le module Modération, passer au module suivant
          this.showGamesModule();
        }
      } else if (this.currentStep === 'module_games') {
        // Gestion des réponses pour le module Jeux et Interactions
        if (option.value === 'games_yes') {
          this.addBotMessage(`
            <div class="feature-card">
              <div class="feature-icon">🎮</div>
              <div class="feature-text">
                <h4>Module Jeux et Interactions</h4>
                <p>Engagez votre communauté avec des jeux interactifs comme des quiz, des paris virtuels et des commandes personnalisées qui permettent à vos viewers d'interagir avec votre stream.</p>
              </div>
            </div>
          `);
          
          // Passer au module suivant - Intelligence Artificielle
          setTimeout(() => {
            this.showAIModule();
          }, 2000);
        } else {
          // L'utilisateur n'est pas intéressé par le module Jeux, passer au module suivant
          this.showAIModule();
        }
      } else if (this.currentStep === 'module_ai') {
        // Gestion des réponses pour le module IA
        if (option.value === 'ai_yes') {
          this.addBotMessage(`
            <div class="feature-card">
              <div class="feature-icon">🤖</div>
              <div class="feature-text">
                <h4>Module Intelligence Artificielle</h4>
                <p>Utilisez l'IA pour générer du contenu, créer des commandes de sponsoring intelligentes et interagir avec votre audience de manière unique et personnalisée.</p>
              </div>
            </div>
          `);
          
          // Terminer la présentation et proposer d'accéder au dashboard
          setTimeout(() => {
            this.finishModulePresentation();
          }, 2000);
        } else {
          // L'utilisateur n'est pas intéressé par le module IA, terminer la présentation
          this.finishModulePresentation();
        }
      } else if (this.currentStep === 'twitch') {
        // Gestion du bouton de réessai pour l'authentification Twitch
        if (option.value === 'retry_twitch') {
          // Nettoyer les données d'authentification précédentes
          localStorage.removeItem('twitch_auth_code');
          localStorage.removeItem('twitch_auth_state_received');
          localStorage.removeItem('twitch_auth_state');
          localStorage.removeItem('twitch_message_received');
          
          // Relancer l'authentification Twitch
          this.connectTwitch();
        }
      } else if (this.currentStep === 'finished') {
        // Gestion des options après une erreur lors de la finalisation
        if (option.value === 'login') {
          // Redirection vers la page de connexion
          this.$router.push('/login');
        } else if (option.value === 'restart') {
          // Réinitialiser le processus d'inscription
          this.messages = [];
          this.messageQueue = [];
          this.isProcessingQueue = false;
          this.discordData = null;
          this.twitchData = null;
          this.cguAccepted = false;
          // Redémarrer la conversation
          this.startConversation();
        }
      }
    },

    connectDiscord() {
      // Ouvrir une fenêtre popup pour l'authentification Discord
      const clientId = '911047298262376460';
      const redirectUri = encodeURIComponent(process.env.VUE_APP_CALLBACK_URL);
      
      // Définir les scopes nécessaires pour une connexion simple
      const scopes = [
        'identify',           // Pour identifier l'utilisateur
      ];
      
      // Construire l'URL d'authentification Discord
      const discordOAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scopes.join('%20')}`;

      // Définir les dimensions et position de la fenêtre popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Ouvrir la fenêtre popup
      const popup = window.open(
        discordOAuthUrl,
        'Connexion Discord',
        `width=${width},height=${height},left=${left},top=${top},location=yes,resizable=yes,scrollbars=yes`
      );

      // Démarrer la vérification de l'authentification
      this.startAuthCheckInterval(popup);

      // Message pour l'utilisateur
      this.addBotMessage("Une fenêtre de connexion Discord s'est ouverte. Veuillez vous connecter et autoriser l'accès.");
    },

    startAuthCheckInterval(popup) {
      // Vérifier toutes les secondes si l'utilisateur s'est connecté
      const checkInterval = setInterval(() => {
        // Vérifier si la fenêtre est fermée
        if (popup.closed) {
          clearInterval(checkInterval);

          // Vérifier si l'utilisateur s'est connecté en vérifiant le token dans le localStorage
          const token = localStorage.getItem('discord_token');
          if (token) {
            // Récupérer les informations utilisateur
            this.fetchDiscordUserInfo(token);
          } else {
            // L'utilisateur a fermé la fenêtre sans se connecter
            this.addBotMessage("Il semble que vous n'avez pas terminé la connexion Discord. Veuillez réessayer.");
          }
        }
      }, 1000);
    },

    async fetchDiscordUserInfo(token) {
      try {
        // Récupérer les informations de l'utilisateur Discord avec le token
        const response = await fetch('https://discord.com/api/users/@me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des données utilisateur Discord');
        }

        const userData = await response.json();

        // Créer l'URL de l'avatar
        let avatarUrl = null;
        if (userData.avatar) {
          avatarUrl = `https://cdn.discordapp.com/avatars/${userData.id}/${userData.avatar}.png`;
        } else {
          // Avatar par défaut si l'utilisateur n'en a pas
          const defaultAvatarNumber = parseInt(userData.discriminator) % 5;
          avatarUrl = `https://cdn.discordapp.com/embed/avatars/${defaultAvatarNumber}.png`;
        }

        // Créer un objet utilisateur avec les informations nécessaires
        const userObject = {
          id: userData.id,
          username: userData.username,
          global_name: userData.global_name,
          discriminator: userData.discriminator,
          avatar: avatarUrl,
          email: userData.email
        };

        // Stocker les informations de l'utilisateur
        localStorage.setItem('discord_user', JSON.stringify(userObject));
        this.discordData = { token, user: userObject };

        // Informer l'utilisateur de la connexion réussie
        this.addUserMessage("Je me suis connecté avec Discord");
        
        // Vérifier si l'utilisateur a déjà une souscription en interrogeant l'API
        try {
          // Appel à l'API avec l'URL complète pour éviter les problèmes de proxy
          const apiUrl = `${process.env.VUE_APP_API_URL}/api/Subscriptions`;
          
          const apiResponse = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (apiResponse.ok) {
            const responseData = await apiResponse.json();
            
            // Si la réponse est positive, l'utilisateur a déjà un compte
            if (responseData && responseData.success) {
              // Afficher un message à l'utilisateur
              this.addBotMessage(`Bonjour <span style="color: #eda526; font-weight: bold;">${userObject.global_name || userObject.username}</span> ! J'ai détecté que vous avez déjà un compte BuddiBot.`);
              this.addBotMessage("Vous allez être redirigé vers votre tableau de bord.");
              
              // Supprimer le flag d'inscription puisque l'utilisateur a déjà un compte
              localStorage.removeItem('is_registering');
              
              // Redirection vers le dashboard après un court délai
              setTimeout(() => {
                this.$router.push('/dashboard');
              }, 3000);
              return;
            }
          }
        } catch (apiError) {
          // Erreur ignorée intentionnellement - on continue avec l'authentification Twitch même en cas d'échec de vérification
        }
        
        this.startTwitchAuth();
      } catch (error) {
        //console.error('Erreur lors de l\'authentification Discord:', error);
        this.addBotMessage("Une erreur s'est produite lors de la connexion avec Discord. Veuillez réessayer.");
      }
    },

    async handleDiscordTokenCallback(token) {
      try {
        this.addUserMessage("Je me suis connecté avec Discord");

        // Stocker le token Discord
        localStorage.setItem('discord_token', token);

        // Récupérer les informations de l'utilisateur Discord avec le token
        const response = await fetch('https://discord.com/api/users/@me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des données utilisateur Discord');
        }

        const userData = await response.json();

        // Créer l'URL de l'avatar
        let avatarUrl = null;
        if (userData.avatar) {
          avatarUrl = `https://cdn.discordapp.com/avatars/${userData.id}/${userData.avatar}.png`;
        } else {
          // Avatar par défaut si l'utilisateur n'en a pas
          const defaultAvatarNumber = parseInt(userData.discriminator) % 5;
          avatarUrl = `https://cdn.discordapp.com/embed/avatars/${defaultAvatarNumber}.png`;
        }

        // Créer un objet utilisateur avec les informations nécessaires
        const userObject = {
          id: userData.id,
          username: userData.username,
          global_name: userData.global_name,
          discriminator: userData.discriminator,
          avatar: avatarUrl,
          email: userData.email
        };

        // Stocker les informations de l'utilisateur
        localStorage.setItem('discord_user', JSON.stringify(userObject));
        this.discordData = { token, user: userObject };

        // Vérifier si l'utilisateur est en cours d'inscription ou non
        const isRegistering = localStorage.getItem('is_registering') === 'true';
        
        // Vérifier si l'utilisateur a déjà une souscription en interrogeant l'API
        try {
          //console.log('Vérification d\'une souscription existante pour l\'utilisateur Discord:', userObject.id);
          
          // Le token est déjà ajouté automatiquement par l'intercepteur axios
          const apiResponse = await api.get('/Subscriptions');
          //console.log('Réponse de l\'API pour la vérification de souscription:', apiResponse);

          // Si la réponse est positive, l'utilisateur a déjà un compte
          if (apiResponse.data && apiResponse.data.success) {
            // Si l'utilisateur n'est pas en cours d'inscription, le rediriger vers le dashboard
            if (!isRegistering) {
              // Afficher un message à l'utilisateur
              this.addBotMessage(`Bonjour <span style="color: #eda526; font-weight: bold;">${userObject.global_name || userObject.username}</span> ! J'ai détecté que vous avez déjà un compte BuddiBot.`);
              this.addBotMessage("Vous allez être redirigé vers votre tableau de bord.");
              
              // Redirection vers le dashboard après un court délai
              setTimeout(() => {
                this.$router.push('/dashboard');
              }, 3000);
              return;
            } else {
              // Si l'utilisateur est en cours d'inscription, l'informer qu'il a déjà un compte mais continuer le processus
              //console.log('L\'utilisateur a déjà un compte mais est en cours d\'inscription');
            }
          }
        } catch (apiError) {
          // Afficher l'erreur complète dans la console pour le débogage
          console.error('Erreur complète lors de la vérification de souscription:', apiError);
          
          // Si l'erreur est 404 (Not Found), cela signifie que l'utilisateur n'a pas de compte
          if (apiError.response?.status === 404) {
            //console.log('Aucune souscription existante trouvée, poursuite du tunnel d\'inscription');
          } else {
            // Pour les autres erreurs, afficher un message mais continuer le processus
            console.warn('Erreur lors de la vérification de souscription:', 
              apiError.response?.status, apiError.response?.data);
          }
        }

        // Si l'utilisateur n'a pas de compte, passer à l'étape suivante
        this.startTwitchAuth();
      } catch (error) {
        console.error('Erreur lors de l\'authentification Discord:', error);
        this.addBotMessage("Une erreur s'est produite lors de la connexion avec Discord. Veuillez réessayer.");
        this.currentStep = 'discord';
      }
    },

    connectTwitch() {
  // Implémentation du flux d'autorisation OAuth de Twitch (Authorization code grant flow)
  const clientId = process.env.VUE_APP_TWITCH_CLIENT_ID;
  
  // Utiliser l'URI de redirection depuis les variables d'environnement ou utiliser une valeur par défaut
  const defaultRedirectUri = 'http://localhost:8080/twitch-callback';
  const redirectUriValue = process.env.VUE_APP_TWITCH_REDIRECT_URI || defaultRedirectUri;
  const redirectUri = encodeURIComponent(redirectUriValue);
  //console.log('URI de redirection Twitch:', redirectUriValue);
  
  // Générer un état unique pour prévenir les attaques CSRF
  const state = 'twitch_auth_' + Math.random().toString(36).substring(2, 15);
  //console.log('Génération de l\'état Twitch:', state);
  
  // Indiquer que la fenêtre Twitch est ouverte
  localStorage.setItem('twitch_window_open', 'true');
  
  // S'assurer que l'état est bien sauvegardé dans le localStorage avant d'ouvrir la popup
  localStorage.removeItem('twitch_auth_state'); // Supprimer l'ancien état s'il existe
  localStorage.setItem('twitch_auth_state', state);
  
  // Définir les paramètres pour l'URL d'authentification Twitch
  const responseType = 'code';

  // Définir les scopes nécessaires pour le fonctionnement du bot
  const scope = [
    'channel:bot',
    'channel:manage:moderators',
    'channel:read:redemptions',
    'channel:read:subscriptions',
    'channel:moderate',
    'moderation:read',
    'moderator:manage:banned_users',
    'moderator:read:chatters',
    'moderator:read:followers',
    'user:bot',
    'user:read:email'
  ];
  
  // Construire l'URL d'authentification Twitch
  const authUrl = `https://id.twitch.tv/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope.join('%20')}&state=${state}`;

  
      // Ouvrir une fenêtre popup pour l'authentification
      const popup = window.open(
        authUrl,
        'Connexion Twitch',
        'width=600,height=700,left=200,top=100'
      );

      // Démarrer la vérification de l'authentification
      this.startTwitchAuthCheckInterval(popup);

      // Message pour l'utilisateur
      this.addBotMessage("Une fenêtre de connexion Twitch s'est ouverte. Veuillez vous connecter et autoriser l'accès.");
    },

    startTwitchAuthCheckInterval(popup) {
      //console.log('Démarrage de la vérification de l\'authentification Twitch...');
      
      // Marquer que la fenêtre Twitch est ouverte
      localStorage.setItem('twitch_window_open', 'true');
      
      // Indiquer si l'authentification a réussi
      let authenticationSuccessful = false;
      
      // Récupérer l'état sauvegardé avant de commencer la vérification
      const savedState = localStorage.getItem('twitch_auth_state');
      //console.log('État sauvegardé au début de la vérification:', savedState);
      
      // Stocker temporairement le code Twitch s'il est déjà dans l'URL
      let storedTwitchCode = null;
      
      // Vérifier si un code Twitch est présent dans l'URL de la fenêtre principale
      const urlParams = new URLSearchParams(window.location.search);
      const urlCode = urlParams.get('code');
      const urlState = urlParams.get('state');
      
      if (urlCode && urlState && urlState === savedState) {
        //console.log('Code Twitch trouvé dans l\'URL principale, stockage temporaire...');
        storedTwitchCode = urlCode;
        // Nettoyer l'URL sans traiter le code tout de suite
        window.history.replaceState({}, document.title, '/register');
      }
      
      // Vérifier toutes les secondes si l'utilisateur s'est connecté
      const checkInterval = setInterval(() => {
        // Vérifier si l'authentification a déjà réussi
        if (authenticationSuccessful || this.twitchData) {
          clearInterval(checkInterval);
          //console.log('Authentification Twitch déjà réussie, arrêt des vérifications');
          return;
        }
        
        // Vérifier si la fenêtre est fermée
        if (popup.closed) {
          clearInterval(checkInterval);
          //console.log('Fenêtre popup fermée, vérification des données d\'authentification...');

          // Marquer la fenêtre Twitch comme fermée
          localStorage.setItem('twitch_window_open', 'false');

          // Récupérer à nouveau l'état sauvegardé (au cas où il aurait changé)
          const currentSavedState = localStorage.getItem('twitch_auth_state');
          //console.log('État sauvegardé après fermeture de la popup:', currentSavedState);
          
          // Vérifier si l'authentification a déjà réussi
          if (this.twitchData) {
            //console.log('Authentification Twitch déjà traitée, aucune action supplémentaire nécessaire');
            return;
          }
          
          // Utiliser le code stocké temporairement ou vérifier à nouveau l'URL
          if (storedTwitchCode) {
            //console.log('Utilisation du code Twitch stocké temporairement');
            authenticationSuccessful = true;
            this.handleTwitchCallback(storedTwitchCode);
          } else {
            // Vérifier à nouveau si un code Twitch est présent dans l'URL
            const newUrlParams = new URLSearchParams(window.location.search);
            const newUrlCode = newUrlParams.get('code');
            const newUrlState = newUrlParams.get('state');
            
            //console.log('Vérification de l\'URL principale après fermeture:', { newUrlCode, newUrlState, savedState: currentSavedState });
            
            if (newUrlCode && newUrlState && newUrlState === currentSavedState) {
              //console.log('Code trouvé dans l\'URL principale après fermeture, traitement...');
              // Nettoyer l'URL
              window.history.replaceState({}, document.title, '/register');
              // Traiter le code d'authentification Twitch
              authenticationSuccessful = true;
              this.handleTwitchCallback(newUrlCode);
            }
          }
          // Supprimer l'état sauvegardé
        localStorage.removeItem('twitch_auth_state');
        return;
      }

      // Si aucun code n'est présent dans l'URL, vérifier dans le localStorage
      // Ces valeurs sont normalement définies par le composant handlePopupMessage.js
      const code = localStorage.getItem('twitch_auth_code');
      const receivedState = localStorage.getItem('twitch_auth_state_received');
      const savedState = localStorage.getItem('twitch_auth_state');
      
      // Vérifier si le code est présent et si l'état correspond
      if (code && (receivedState === savedState || localStorage.getItem('twitch_message_received') === 'true')) {
        // Traiter le code d'authentification Twitch
        this.handleTwitchCallback(code);
        
        // Supprimer les données d'authentification du localStorage
        localStorage.removeItem('twitch_auth_code');
        localStorage.removeItem('twitch_auth_state_received');
        localStorage.removeItem('twitch_auth_state');
        localStorage.removeItem('twitch_message_received');
        return;
      }
      
      // Si nous avons un code mais pas d'état correspondant, essayons quand même
      if (code) {
        //console.log('Code trouvé sans état correspondant, tentative de traitement...');
        this.handleTwitchCallback(code);
        
        // Supprimer les données d'authentification du localStorage
        localStorage.removeItem('twitch_auth_code');
        localStorage.removeItem('twitch_auth_state_received');
        localStorage.removeItem('twitch_auth_state');
        localStorage.removeItem('twitch_message_received');
        return;
      }
      
      //console.log('Aucun code valide trouvé, vérification périodique...');
      // Vérifier toutes les 2 secondes pendant 10 secondes si le code apparaît
      let attempts = 0;
      const maxAttempts = 5;
      
      const checkCodeInterval = setInterval(() => {
        // Vérifier à nouveau l'URL principale
        const urlParams = new URLSearchParams(window.location.search);
        const urlCode = urlParams.get('code');
        const urlState = urlParams.get('state');
        const currentSavedState = localStorage.getItem('twitch_auth_state');
                
        if (urlCode && urlState && urlState === currentSavedState) {
          clearInterval(checkCodeInterval);
          //console.log('Code trouvé dans l\'URL principale après attente, traitement...');
          // Nettoyer l'URL
          window.history.replaceState({}, document.title, '/register');
          // Traiter le code d'authentification Twitch
          this.handleTwitchCallback(urlCode);
          // Supprimer l'état sauvegardé
          localStorage.removeItem('twitch_auth_state');
          return;
        }
        
        // Vérifier à nouveau le localStorage
        const code = localStorage.getItem('twitch_auth_code');
        const receivedState = localStorage.getItem('twitch_auth_state_received');
        
        if (code && (receivedState === currentSavedState || localStorage.getItem('twitch_message_received') === 'true')) {
          clearInterval(checkCodeInterval);
          //console.log('Code trouvé dans le localStorage après attente, traitement...');
          // Traiter le code d'authentification Twitch
          this.handleTwitchCallback(code);
          
          // Supprimer les données d'authentification du localStorage
          localStorage.removeItem('twitch_auth_code');
          localStorage.removeItem('twitch_auth_state_received');
          localStorage.removeItem('twitch_auth_state');
          localStorage.removeItem('twitch_message_received');
          return;
        }
        
        // Si nous avons un code mais pas d'état correspondant, essayons quand même
        if (code) {
          clearInterval(checkCodeInterval);
          //console.log('Code trouvé sans état correspondant après attente, tentative de traitement...');
          this.handleTwitchCallback(code);
          
          // Supprimer les données d'authentification du localStorage
          localStorage.removeItem('twitch_auth_code');
          localStorage.removeItem('twitch_auth_state_received');
          localStorage.removeItem('twitch_auth_state');
          localStorage.removeItem('twitch_message_received');
          return;
        }
        
        if (++attempts >= maxAttempts) {
          clearInterval(checkCodeInterval);
          console.warn('Nombre maximum de tentatives atteint, vérification finale...');
          
          // Vérifier si l'authentification a déjà réussi
          if (authenticationSuccessful || this.twitchData) {
            //console.log('Authentification Twitch déjà réussie, aucune action supplémentaire nécessaire');
            return;
          }
          
          // Dernière vérification du localStorage avant d'abandonner
          const finalCode = localStorage.getItem('twitch_auth_code');
          if (finalCode) {
            //console.log('Code trouvé dans le localStorage lors de la vérification finale, traitement...');
            authenticationSuccessful = true;
            this.handleTwitchCallback(finalCode);
            return;
          }
          
          // L'utilisateur a fermé la fenêtre sans se connecter
          this.addBotMessage("Il semble que vous n'avez pas terminé la connexion Twitch. Veuillez réessayer.");
          
          // Proposer un bouton pour réessayer
          this.currentOptions = [
            { text: "Réessayer", value: 'retry_twitch', primary: true }
          ];
          
          // Mettre à jour le gestionnaire d'options pour gérer le bouton de réessai
          this.currentStep = 'twitch';
        }
      }, 2000);
    }, 1000);
    },

    async handleTwitchCallback(code) {
      //console.log('Traitement du code Twitch:', code);
      
      if (!code) {
        console.error('Aucun code Twitch fourni');
        this.addBotMessage("Une erreur s'est produite lors de la connexion avec Twitch. Aucun code d'authentification n'a été reçu.");
        return;
      }
      
      // Vérifier si nous avons déjà traité ce code pour éviter les doublons
      if (this.twitchData && this.twitchData.code === code) {
        //console.log('Code Twitch déjà traité, passage à létape suivante');
        // Si le code a déjà été traité mais que nous sommes encore à l'étape Twitch,
        // passons à l'étape suivante pour éviter de bloquer l'utilisateur
        if (this.currentStep === 'twitch') {
          this.showFeatures();
        }
        return;
      }
      
      // Nettoyer les indicateurs de fenêtre
      localStorage.removeItem('twitch_window_open');
      
      //console.log('Traitement du code Twitch en cours...');
      
      // Ajouter le message utilisateur
      this.addUserMessage("Je me suis connecté avec Twitch");
      
      // Stocker les informations Twitch
      this.twitchData = { code };

      try {
        // Envoyer la requête de création de compte après l'authentification Twitch
        await this.createSubscription();
        
        // Lancer la présentation des modules
        this.showFeatures();
        
        // Nettoyer le localStorage seulement après avoir complété le processus avec succès
        setTimeout(() => {
          localStorage.removeItem('twitch_auth_code');
          localStorage.removeItem('twitch_auth_state');
          localStorage.removeItem('twitch_auth_state_received');
          localStorage.removeItem('twitch_message_received');
          localStorage.removeItem('twitch_window_open');
          //console.log('Nettoyage des états d\'authentification Twitch terminé');
        }, 2000);
      } catch (error) {
        console.error('Erreur lors du traitement de l\'authentification Twitch:', error);
        this.addBotMessage("Une erreur s'est produite lors du traitement de votre authentification Twitch. Veuillez réessayer.");
        
        // Proposer un bouton pour réessayer
        this.currentOptions = [
          { text: "Réessayer", value: 'retry_twitch', primary: true }
        ];
      }
    },
  }
}
</script>

<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #015a66 0%, #016e7e 100%);
}

.chat-container {
  width: 100%;
  max-width: 800px;
  height: 80vh;
  background: rgba(1, 110, 126, 0.15);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.chat-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: rgba(1, 110, 126, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.bot-avatar img {
  width: 48px;
  height: 48px;
  margin-right: 16px;
  object-fit: contain;
  background-color: transparent;
}

.chat-header h1 {
  color: white;
  font-size: 1.5rem;
  margin: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message {
  display: flex;
  margin-bottom: 16px;
  max-width: 80%;
}

.bot-message {
  align-self: flex-start;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.bot-avatar-small img {
  width: 32px;
  height: 32px;
  margin-right: 12px;
  object-fit: contain;
  background-color: transparent;
}

.message-content {
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 16px;
  border-radius: 12px;
  color: white;
  line-height: 1.5;
}

.bot-message .message-content {
  background: rgba(1, 110, 126, 0.6);
  border-radius: 0 12px 12px 12px;
  border-left: 3px solid #016e7e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.user-message .message-content {
  background: rgba(237, 165, 38, 0.5);
  border-radius: 12px 0 12px 12px;
  margin-right: 12px;
  border-right: 3px solid #eda526;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.typing-indicator {
  display: flex;
  align-items: center;
}

.typing-dots {
  display: flex;
  padding: 12px 16px;
  background: rgba(1, 110, 126, 0.6);
  border-radius: 0 12px 12px 12px;
  border-left: 3px solid #016e7e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.typing-dots span {
  height: 8px;
  width: 8px;
  margin: 0 4px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  display: inline-block;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-dots span:nth-child(1) {
  animation-delay: 0s;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0% {
    transform: scale(0);
    opacity: 0.8;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0.8;
  }
}

.chat-interaction {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(1, 110, 126, 0.3);
}

.options-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

.option-button {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.option-button.primary {
  background: #016e7e;
  border-color: #016e7e;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.option-button.primary:hover {
  background: #015a66;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.auth-container {
  display: flex;
  justify-content: center;
  margin: 16px 0;
}

.discord-button, .twitch-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  width: 250px;
}

.discord-button {
  background-color: #5865F2; /* Couleur officielle de Discord */
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border: none;
}

.twitch-button {
  background-color: #9146FF; /* Couleur officielle de Twitch */
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border: none;
}

.discord-button:hover {
  background-color: #4752C4;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.twitch-button:hover {
  background-color: #7D3FD3; /* Version plus foncée de la couleur Twitch */
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.discord-logo, .twitch-logo {
  width: 20px;
  height: 20px;
}

/* Animations pour les bulles de conversation */
.message-fade-enter-active {
  animation: message-appear 0.5s ease-out forwards;
  transform-origin: top left;
}

.message-fade-leave-active {
  animation: message-disappear 0.3s ease-in forwards;
}

@keyframes message-appear {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  50% {
    opacity: 0.5;
    transform: translateY(5px) scale(0.97);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes message-disappear {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
}

.message-content {
  transition: all 0.2s ease;
}

.progress-bar {
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  background: rgba(1, 110, 126, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.progress-step {
  position: relative;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0 12px;
}

.progress-step::before {
  content: '';
  position: absolute;
  top: -24px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.progress-step::after {
  content: '';
  position: absolute;
  top: -20px;
  left: 0;
  width: 100%;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  z-index: -1;
}

.progress-step:first-child::after {
  width: 50%;
  left: 50%;
}

.progress-step:last-child::after {
  width: 50%;
  right: 50%;
}

.progress-step.active {
  color: white;
}

.progress-step.active::before {
  background: #016e7e;
  border-color: #016e7e;
  box-shadow: 0 0 8px rgba(1, 110, 126, 0.6);
}

.progress-step.completed::before {
  background: #eda526;
  border-color: #eda526;
}

.progress-step.completed::after {
  background: #eda526;
}

/* Styles pour les conteneurs spéciaux */
.cgu-container, .steps-container {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin: 10px 0;
  text-align: left;
}

.cgu-container h3, .steps-container h3 {
  margin-top: 0;
  color: #eda526;
  font-size: 1.2em;
}

.cgu-container ul, .steps-container ul {
  padding-left: 20px;
  margin: 10px 0;
}

.cgu-container li, .steps-container li {
  margin-bottom: 5px;
}

.cgu-container a {
  color: #eda526;
  text-decoration: underline;
  font-weight: 500;
}

.cgu-container a:hover {
  text-decoration: underline;
  color: #eda526;
}

.features-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.features-container h3 {
  margin-top: 0;
  color: #eda526;
}

.feature-card {
  display: flex;
  align-items: center;
  background: rgba(1, 110, 126, 0.15);
  padding: 12px;
  border-radius: 8px;
  gap: 16px;
  border-left: 3px solid #016e7e;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateX(5px);
  background: rgba(1, 110, 126, 0.25);
}

.feature-icon {
  font-size: 2rem;
  background: rgba(237, 165, 38, 0.2);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #eda526;
}

.feature-text {
  flex: 1;
}

.feature-text h4 {
  margin: 0 0 4px 0;
  color: white;
}

.feature-text p {
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}
</style>
